import React, { useEffect, useMemo, useState } from 'react';
import styles from './ImagePickerField.module.scss';
import ImagePickerModal, { ImagePickerSelection } from './ImagePickerModal';
import { getVariantLabel } from '../../../../utils/image.utils';

interface ImagePickerFieldProps {
    label: string;
    value: string;
    onChange: (next: string) => void;
    token?: string | null;
    required?: boolean;
    helperText?: string;
    placeholder?: string;
    preferredVariantType?: number | null;
}

const ImagePickerField = ({
    label,
    value,
    onChange,
    token,
    required,
    helperText,
    placeholder,
    preferredVariantType
}: ImagePickerFieldProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selection, setSelection] = useState<ImagePickerSelection | null>(null);

    useEffect(() => {
        if (!value) {
            setSelection(null);
            return;
        }

        if (selection && selection.url === value) {
            return;
        }

        setSelection(null);
    }, [value]);

    const previewUrl = useMemo(() => {
        if (selection?.thumbnailUrl) {
            return selection.thumbnailUrl;
        }
        if (selection?.url) {
            return selection.url;
        }
        if (value?.length > 0) {
            return value;
        }
        return null;
    }, [selection, value]);

    return (
        <div className={styles.field}>
            <div className={styles.labelRow}>
                <label>{label}</label>
                <button type="button" className={styles.secondary} onClick={() => setIsOpen(true)}>
                    Choose from library
                </button>
            </div>
            <input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                placeholder={placeholder}
                required={required}
                maxLength={2000}
            />
            {helperText ? <div className={styles.helper}>{helperText}</div> : null}
            {selection ? (
                <div className={styles.selectionNote}>
                    Selected: {selection.title ?? 'Untitled'} - {getVariantLabel(selection.variantType)}
                </div>
            ) : null}
            {previewUrl ? (
                <div className={styles.preview}>
                    <img src={previewUrl} alt={selection?.title ?? 'Selected image'} />
                </div>
            ) : null}
            {value ? (
                <button type="button" className={styles.clear} onClick={() => onChange('')}>
                    Clear image
                </button>
            ) : null}

            <ImagePickerModal
                isOpen={isOpen}
                token={token}
                onClose={() => setIsOpen(false)}
                onSelect={(picked) => {
                    setSelection(picked);
                    onChange(picked.url);
                }}
                preferredVariantType={preferredVariantType}
            />
        </div>
    );
};

export default ImagePickerField;
